import { memo } from 'react';
import { Button, Skeleton } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { TriangleAlert } from 'lucide-react';

import { LINK_STATE } from '../constants/invitationStates.js';
import AcceptInvitationScreen from './AcceptInvitationScreen';
import InvitationOtherAccountScreen from './InvitationOtherAccountScreen';
import InvitationShell from './InvitationShell';
import InvitationStatusScreen from './InvitationStatusScreen';

/**
 * Picks the recipient screen for an invitation link. Presentational: it renders the state it is
 * given and implements no sequencing (that is `useAcceptInvitation`, CT-5567).
 *
 * @param {object} props
 * @param {boolean} [props.loading]
 * @param {Error|null} [props.error] The lookup failed.
 * @param {string|null} [props.linkState] One of `LINK_STATE`.
 * @param {{ workspaceName: string, invitedEmail?: string, welcomeMessage?: string|null }} [props.invitation]
 * @param {() => void} [props.onConfirm]
 * @param {boolean} [props.confirming]
 * @param {boolean} [props.hasAcceptError]
 * @param {string|null} [props.signedInEmail] Email of the current session (for the `otherAccount` state).
 * @param {() => void} [props.onSignOut]
 * @param {() => void} [props.onStay]
 * @param {() => void} [props.onRetry]
 */
const InvitationScreen = ({
  loading = false,
  error = null,
  linkState = null,
  invitation,
  onConfirm,
  confirming = false,
  hasAcceptError = false,
  signedInEmail = null,
  onSignOut,
  onStay,
  onRetry,
}) => {
  const { t } = useTranslation('people-access');

  if (loading) {
    return (
      <InvitationShell>
        <Skeleton width={32} height={32} />
        <p className="m-0 text-sm text-content-muted" role="status">
          {t('invitation.loading')}
        </p>
      </InvitationShell>
    );
  }

  if (error) {
    return (
      <InvitationShell>
        <TriangleAlert size={32} className="text-content-muted" aria-hidden />
        <div>
          <h1 className="m-0 text-xl font-semibold text-content">{t('invitation.error.title')}</h1>
          <p className="m-0 mt-1 text-sm text-content-muted">{t('invitation.error.subtitle')}</p>
        </div>
        <Button variant="secondary" onClick={onRetry}>
          {t('invitation.error.retry')}
        </Button>
      </InvitationShell>
    );
  }

  if (linkState === LINK_STATE.VALID && invitation) {
    return (
      <AcceptInvitationScreen
        workspaceName={invitation.workspaceName}
        invitedEmail={invitation.invitedEmail}
        welcomeMessage={invitation.welcomeMessage}
        onConfirm={onConfirm}
        confirming={confirming}
        hasError={hasAcceptError}
      />
    );
  }

  if (linkState === LINK_STATE.OTHER_ACCOUNT && invitation) {
    return (
      <InvitationOtherAccountScreen
        invitedEmail={invitation.invitedEmail}
        signedInEmail={signedInEmail}
        onSignOut={onSignOut}
        onStay={onStay}
      />
    );
  }

  return <InvitationStatusScreen variant={linkState ?? LINK_STATE.INVALID} />;
};

export default memo(InvitationScreen);
